import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import { IconWalls } from "$root/components/IconWall";
import { currencyCode, currencyMinorUnits } from "$root/utils/currency";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useMappedQuery, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import { type ContextContent, dateYYYYMMDD, withContext } from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import type { BondTrackerTab, BondPaymentType } from "@eu.mdotm.api/sphere";
import {
	type TableColumn,
	BaseTable,
	DatePickerRangeInput,
	DebouncedSearchInput,
	DefaultTabTitle,
	Tab,
	TabGroup,
} from "@mdotm/shadcn/legacy/components";
import { type SelectOption, Pagination, Select } from "@mdotm/shadcn/mdotm/components";
import { useEffect, useMemo, useRef, useState } from "react";
import { toBondDividendRow, type BondDividendRow } from "./data";

export type { BondDividendRow } from "./data";

const TYPE_OPTIONS: SelectOption<BondPaymentType | "all">[] = [
	{ label: "All types", value: "all" },
	{ label: "Coupon", value: "COUPON" },
	{ label: "Dividend", value: "DIVIDEND" },
	// TODO(MDOT-17017): enable once CORE and BE propagate the data
	// { label: "Maturity", value: "MATURITY" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

/**
 * `cashCurrency` is set (`null` = unset, shown as the default currency) for a quantities portfolio,
 * where `deltaCash` arrives as an amount in that currency and replaces the percentage column. The
 * currency code is in the column header only.
 */
export function buildColumns(cashCurrency?: Currencies | null): TableColumn<BondDividendRow>[] {
	return [
		{ ...commonColumns.date("date"), header: "Date" },
		{ ...commonColumns.string("name"), header: "Name" },
		{ ...commonColumns.string("type"), header: "Type" },
		{ ...commonColumns.percentage("couponRate", 2, "N/A"), header: "Rate / Yield" },
		cashCurrency === undefined
			? { ...commonColumns.percentage("deltaCash", 2, "N/A"), header: "Delta cash" }
			: {
					...commonColumns.float("deltaCash", currencyMinorUnits(cashCurrency), "N/A"),
					header: `Monetary value (${currencyCode(cashCurrency)})`,
				},
	];
}

// DatePickerRangeInput value: [] (none) | [from] | [from, to], all ISO YYYY-MM-DD strings.
type DateRange = [] | [string] | [string, string];

/**
 * Presentational toolbar + table + pagination for a single tab of bond/dividend events. Fully
 * controlled — the container owns filter/pagination state and feeds it the current page of rows.
 * The API does not expose a sort parameter, so columns are intentionally not client-sortable
 * (sorting only the visible page would misrepresent the full result set).
 */
export function TrackerSectionView({
	cashCurrency,
	rows,
	totalResults,
	onQueryChange,
	dateRange,
	onDateRangeChange,
	type,
	onTypeChange,
	page,
	onPageChange,
	pageSize,
	onPageSizeChange,
}: {
	/** Currency symbol of the amount column; omitted on a weights portfolio, which shows the percentage. */
	cashCurrency?: Currencies | null;
	rows: BondDividendRow[];
	totalResults: number;
	onQueryChange: (query: string) => void;
	dateRange: DateRange;
	onDateRangeChange: (range: DateRange) => void;
	type: BondPaymentType | "all";
	onTypeChange: (type: BondPaymentType | "all") => void;
	page: number;
	onPageChange: (page: number) => void;
	pageSize: number;
	onPageSizeChange: (pageSize: number) => void;
}): React.JSX.Element {
	const columns = useMemo(() => buildColumns(cashCurrency), [cashCurrency]);
	return (
		<div className="flex flex-col min-h-0 grow">
			<div className="flex items-center gap-2 mb-2 shrink-0">
				<DebouncedSearchInput
					className="flex-1"
					externalQuery=""
					placeholder="Search by name or type..."
					onChange={onQueryChange}
				/>
				<DatePickerRangeInput max={dateYYYYMMDD(new Date())} value={dateRange} onChange={onDateRangeChange} />
				<Select
					className="w-40"
					options={TYPE_OPTIONS}
					value={type}
					onChange={(value) => onTypeChange(value ?? "all")}
				/>
			</div>
			<BaseTable
				rows={rows}
				columns={columns}
				headerRowClassName="sticky top-0 z-10 bg-white"
				noDataText="No data available"
			/>
			<Pagination
				className="pt-2 shrink-0"
				totalResults={totalResults}
				currentPage={page}
				onPageChange={onPageChange}
				pageSize={pageSize}
				pageSizeOptions={PAGE_SIZE_OPTIONS}
				onPageSizeChange={(size) => {
					onPageSizeChange(size);
					onPageChange(1);
				}}
			/>
		</div>
	);
}

/** One server page of events, as the section view reads it. */
type TrackerSectionData = { rows: BondDividendRow[]; totalResults: number };

/** Owns the filter/pagination state for one tab and fetches the matching server page. */
function TrackerSection({
	investmentUuid,
	tab,
	asOf,
	readable,
	cashCurrency,
}: {
	investmentUuid: string;
	tab: BondTrackerTab;
	cashCurrency?: Currencies | null;
	asOf?: string;
	/** Whether the pinned date's snapshot can be read yet; always true in the live view. */
	readable: boolean;
}): React.JSX.Element {
	const [query, setQuery] = useState("");
	const [dateRange, setDateRange] = useState<DateRange>([]);
	const [type, setType] = useState<BondPaymentType | "all">("all");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

	const [from, to] = dateRange;
	const cashFlowsQuery = useQueryNoRefetch({
		...portfolioRepository.bondCashFlows.getQueryConfig({
			uuid: investmentUuid,
			tab,
			offset: (page - 1) * pageSize,
			limit: pageSize,
			fromDate: from,
			toDate: to,
			searchName: query.trim() || undefined,
			types: type === "all" ? undefined : [type],
			asOf,
		}),
		enabled: !!investmentUuid && readable,
	});

	// A narrowing filter is active whenever the user typed a search, picked a date range or a type.
	const hasActiveFilters = query.trim() !== "" || dateRange.length > 0 || type !== "all";

	const sectionQuery = useMappedQuery(
		(page): PortfolioWidgetData<TrackerSectionData | undefined> => {
			const totalResults = page.pagination?.total ?? 0;
			// A section with no events for this tab at all — not merely filtered out — has nothing to show.
			// With filters active the table stays so the user can still see and clear the toolbar.
			if (totalResults === 0 && !hasActiveFilters) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: { rows: (page.items ?? []).map(toBondDividendRow), totalResults },
				widgetStatus: WidgetStatus.READY,
			};
		},
		[hasActiveFilters],
		cashFlowsQuery,
	);

	// The base's loading wall covers the initial fetch only: later filter/page changes keep the previous
	// page on screen (keepPreviousData), so the toolbar never flickers back to a wall.
	return (
		<PortfolioQueryWidgetBase query={sectionQuery} iconWalls={{ empty: <IconWalls.NoCashEvents /> }}>
			{({ rows, totalResults }) => (
				<TrackerSectionView
					cashCurrency={cashCurrency}
					rows={rows}
					totalResults={totalResults}
					// Reset to the first page whenever a filter narrows the result window.
					onQueryChange={(q) => {
						setQuery(q);
						setPage(1);
					}}
					dateRange={dateRange}
					onDateRangeChange={(range) => {
						setDateRange(range);
						setPage(1);
					}}
					type={type}
					onTypeChange={(t) => {
						setType(t);
						setPage(1);
					}}
					page={page}
					onPageChange={setPage}
					pageSize={pageSize}
					onPageSizeChange={setPageSize}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
}

// Pinned to a past date the cash-flow endpoint reads the portfolio's snapshot, which answers 404 until
// it is stored — so every read here waits on the same gate the other as-of widgets use.
const BondsDividendsTracker = ({ portfolio, asOf, asOfSnapshot }: ContextContent<typeof PortfolioContext>) => {
	const [currentTab, setCurrentTab] = useState(0);
	// Once the user picks a tab — or we've auto-opened the non-empty one — stop overriding their choice.
	const tabResolvedRef = useRef(false);

	useWidgetOptions(
		() => ({
			title: "Income tracker",
		}),
		[],
	);

	const investmentUuid = portfolio?.uuid ?? "";
	// On a quantities portfolio Core sends `deltaCash` as an amount in the base currency.
	const cashCurrency = portfolio?.quantityMode ? (portfolio.baseCurrency ?? null) : undefined;
	const readable = isAsOfReadable(asOf, asOfSnapshot);

	// Unfiltered first-page probes for both tabs, used only to decide which tab to open on: when one
	// tab has no events at all we start on the other. The params are kept identical to a section's
	// initial fetch (page 1, default page size, no filters) so these share the React Query cache key
	// with the mounted section(s) instead of firing a second request.
	const probeParams = (tab: BondTrackerTab) => ({
		uuid: investmentUuid,
		tab,
		offset: 0,
		limit: PAGE_SIZE_OPTIONS[0],
		fromDate: undefined,
		toDate: undefined,
		searchName: undefined,
		types: undefined,
		asOf,
	});
	const historyProbe = useQueryNoRefetch({
		...portfolioRepository.bondCashFlows.getQueryConfig(probeParams("HISTORY")),
		enabled: !!investmentUuid && readable,
	});
	const upcomingProbe = useQueryNoRefetch({
		...portfolioRepository.bondCashFlows.getQueryConfig(probeParams("UPCOMING")),
		enabled: !!investmentUuid && readable,
	});

	const historyTotal = historyProbe.data?.pagination?.total;
	const upcomingTotal = upcomingProbe.data?.pagination?.total;

	useEffect(() => {
		if (tabResolvedRef.current) {
			return;
		}
		// Wait until both counts are known so we don't flip to a tab that also turns out to be empty.
		if (historyTotal == null || upcomingTotal == null) {
			return;
		}
		tabResolvedRef.current = true;
		if (historyTotal === 0 && upcomingTotal > 0) {
			setCurrentTab(1);
		}
	}, [historyTotal, upcomingTotal]);

	return (
		<div className="flex flex-col min-h-0 grow">
			{/* minHeightStrategy="0" lets the active tab's table shrink to the widget height and scroll internally. */}
			<TabGroup
				minHeightStrategy="0"
				palette="primary"
				tabIndex={currentTab}
				onTabChange={(index) => {
					// A manual switch is the user's final say — lock out the auto-open effect from here on.
					tabResolvedRef.current = true;
					setCurrentTab(index);
				}}
			>
				<Tab
					className="flex flex-col min-h-0 grow"
					title={(titleProps) => <DefaultTabTitle {...titleProps}>History</DefaultTabTitle>}
				>
					<TrackerSection
						investmentUuid={investmentUuid}
						tab="HISTORY"
						asOf={asOf}
						readable={readable}
						cashCurrency={cashCurrency}
					/>
				</Tab>
				<Tab
					className="flex flex-col min-h-0 grow"
					title={(titleProps) => <DefaultTabTitle {...titleProps}>Upcoming</DefaultTabTitle>}
				>
					<TrackerSection
						investmentUuid={investmentUuid}
						tab="UPCOMING"
						asOf={asOf}
						readable={readable}
						cashCurrency={cashCurrency}
					/>
				</Tab>
			</TabGroup>
		</div>
	);
};

export default withContext(PortfolioContext)(BondsDividendsTracker);
